import { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { 
  Users, School, Search, Shield, 
  Trash2, KeyRound, ChevronDown, ChevronUp,
  Plus, Minus, Crown, Home, Star, BarChart3, Eye, UserPlus
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const AdminPage = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('users');
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [schools, setSchools] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [userTypeFilter, setUserTypeFilter] = useState('');
  const [selectedUser, setSelectedUser] = useState(null);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [customCoins, setCustomCoins] = useState('');
  const [analytics, setAnalytics] = useState(null);
  const [analyticsRange, setAnalyticsRange] = useState('7d');

  useEffect(() => {
    checkAdminAccess();
  }, []);

  const checkAdminAccess = async () => {
    try {
      const token = localStorage.getItem('typetown_token');
      if (!token) {
        navigate('/login');
        return;
      }
      
      await axios.get(`${API}/api/admin/stats`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      loadAllData();
    } catch (e) {
      if (e.response?.status === 403) {
        navigate('/dashboard');
      } else {
        navigate('/login');
      }
    }
  };

  const loadAllData = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('typetown_token');
      const headers = { Authorization: `Bearer ${token}` };
      
      const [statsRes, usersRes, schoolsRes, analyticsRes] = await Promise.all([
        axios.get(`${API}/api/admin/stats`, { headers }),
        axios.get(`${API}/api/admin/users?limit=100`, { headers }),
        axios.get(`${API}/api/admin/schools`, { headers }),
        axios.get(`${API}/api/admin/analytics`, { headers })
      ]);
      
      setStats(statsRes.data);
      setUsers(usersRes.data.users);
      setSchools(schoolsRes.data.schools);
      setAnalytics(analyticsRes.data);
    } catch (e) {
      setError('Failed to load data');
    }
    setLoading(false);
  };

  const searchUsers = async () => {
    try {
      const token = localStorage.getItem('typetown_token');
      const params = new URLSearchParams();
      if (searchQuery) params.append('search', searchQuery);
      if (userTypeFilter) params.append('user_type', userTypeFilter);
      params.append('limit', '100');
      
      const res = await axios.get(`${API}/api/admin/users?${params}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUsers(res.data.users);
    } catch (e) {
      setError('Search failed');
    }
  };

  const updateUser = async (userId, updates) => {
    try {
      const token = localStorage.getItem('typetown_token');
      await axios.put(`${API}/api/admin/users/${userId}`, updates, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSuccess('User updated successfully');
      setTimeout(() => setSuccess(''), 3000);
      loadAllData();
      if (selectedUser?.user?.id === userId) {
        viewUserDetails(userId);
      }
    } catch (e) {
      setError(e.response?.data?.detail || 'Update failed');
      setTimeout(() => setError(''), 3000);
    }
  };

  const resetPassword = async (userId) => {
    const newPassword = prompt('Enter new password for user:');
    if (!newPassword) return;
    
    try {
      const token = localStorage.getItem('typetown_token');
      await axios.post(`${API}/api/admin/users/${userId}/reset-password`, 
        { new_password: newPassword },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setSuccess('Password reset successfully');
      setTimeout(() => setSuccess(''), 3000);
    } catch (e) {
      setError('Password reset failed');
      setTimeout(() => setError(''), 3000);
    }
  };

  const deleteUser = async (userId, username) => {
    if (!window.confirm(`Are you sure you want to delete user "${username}"? This cannot be undone.`)) {
      return;
    }
    
    try {
      const token = localStorage.getItem('typetown_token');
      await axios.delete(`${API}/api/admin/users/${userId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSuccess('User deleted');
      setTimeout(() => setSuccess(''), 3000);
      setSelectedUser(null);
      loadAllData();
    } catch (e) {
      setError(e.response?.data?.detail || 'Delete failed');
      setTimeout(() => setError(''), 3000);
    }
  };

  const viewUserDetails = async (userId) => {
    try {
      const token = localStorage.getItem('typetown_token');
      const res = await axios.get(`${API}/api/admin/users/${userId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSelectedUser(res.data);
    } catch (e) {
      setError('Failed to load user details');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-sky-100 via-white to-orange-50 flex items-center justify-center">
        <div className="text-text-dark text-xl font-display">Loading admin panel...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-sky-100 via-white to-orange-50">
      {/* Decorative background */}
      <div className="fixed top-0 left-0 right-0 h-64 bg-gradient-to-b from-sky-200/50 to-transparent pointer-events-none" />
      
      {/* Header */}
      <header className="sticky top-0 z-50 bg-white/90 backdrop-blur-md border-b border-gray-200 shadow-sm px-3 sm:px-6 py-3 sm:py-4">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-2 sm:gap-3">
            <div className="p-2 bg-coral-orange/10 rounded-xl">
              <Shield className="w-6 h-6 sm:w-7 sm:h-7 text-coral-orange" />
            </div>
            <div>
              <h1 className="text-lg sm:text-2xl font-display font-bold">
                <span className="text-mint-green">Type</span>
                <span className="text-coral-orange">Town</span>
                <span className="text-text-dark"> Admin</span>
              </h1>
            </div>
          </div>
          <Link 
            to="/dashboard"
            className="flex items-center gap-2 text-text-medium hover:text-mint-green transition-colors text-sm sm:text-base"
          >
            <Home className="w-4 h-4 sm:w-5 sm:h-5" />
            <span className="hidden sm:inline">Back to Dashboard</span>
            <span className="sm:hidden">Back</span>
          </Link>
        </div>
      </header>

      {/* Alerts */}
      {error && (
        <div className="max-w-7xl mx-auto px-3 sm:px-6 pt-3 sm:pt-4">
          <div className="bg-red-50 border-2 border-red-200 text-red-700 px-3 sm:px-4 py-2 rounded-xl text-sm sm:text-base font-medium">
            {error}
          </div>
        </div>
      )}
      {success && (
        <div className="max-w-7xl mx-auto px-3 sm:px-6 pt-3 sm:pt-4">
          <div className="bg-mint-green/10 border-2 border-mint-green/30 text-mint-green px-3 sm:px-4 py-2 rounded-xl text-sm sm:text-base font-medium">
            {success}
          </div>
        </div>
      )}

      <main className="max-w-7xl mx-auto px-3 sm:px-6 py-4 sm:py-8 relative">
        {/* Stats Cards */}
        {stats && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-2 sm:gap-4 mb-4 sm:mb-8">
            <div className="bg-white rounded-2xl sm:rounded-3xl p-3 sm:p-5 border-2 border-gray-100 shadow-lg">
              <div className="flex items-center gap-2 mb-2">
                <Users className="w-5 h-5 text-coral-orange" />
                <span className="text-text-medium text-xs sm:text-sm font-medium">Total Users</span>
              </div>
              <div className="text-2xl sm:text-4xl font-display font-bold text-text-dark">{stats.total_users}</div>
            </div>
            <div className="bg-white rounded-2xl sm:rounded-3xl p-3 sm:p-5 border-2 border-gray-100 shadow-lg">
              <div className="flex items-center gap-2 mb-2">
                <School className="w-5 h-5 text-sky-blue" />
                <span className="text-text-medium text-xs sm:text-sm font-medium">Schools</span>
              </div>
              <div className="text-2xl sm:text-4xl font-display font-bold text-sky-blue">{stats.total_schools}</div>
            </div>
            <div className="bg-white rounded-2xl sm:rounded-3xl p-3 sm:p-5 border-2 border-gray-100 shadow-lg">
              <div className="flex items-center gap-2 mb-2">
                <Star className="w-5 h-5 text-sunny-yellow fill-sunny-yellow" />
                <span className="text-text-medium text-xs sm:text-sm font-medium">Sessions</span>
              </div>
              <div className="text-2xl sm:text-4xl font-display font-bold text-sunny-yellow">{stats.total_sessions}</div>
            </div>
            <div className="bg-white rounded-2xl sm:rounded-3xl p-3 sm:p-5 border-2 border-gray-100 shadow-lg">
              <div className="flex items-center gap-2 mb-2">
                <Plus className="w-5 h-5 text-mint-green" />
                <span className="text-text-medium text-xs sm:text-sm font-medium">This Week</span>
              </div>
              <div className="text-2xl sm:text-4xl font-display font-bold text-mint-green">+{stats.recent_signups}</div>
            </div>
          </div>
        )}

        {/* Tabs */}
        <div className="flex gap-2 sm:gap-4 mb-4 sm:mb-6">
          <button
            onClick={() => setActiveTab('users')}
            className={`flex items-center gap-1 sm:gap-2 px-4 sm:px-6 py-2.5 sm:py-3 rounded-2xl transition-all font-bold text-sm sm:text-base ${
              activeTab === 'users' 
                ? 'bg-coral-orange text-white shadow-lg' 
                : 'bg-white text-text-medium hover:bg-gray-50 border-2 border-gray-100'
            }`}
          >
            <Users className="w-4 h-4 sm:w-5 sm:h-5" />
            Users
          </button>
          <button
            onClick={() => setActiveTab('schools')}
            className={`flex items-center gap-1 sm:gap-2 px-4 sm:px-6 py-2.5 sm:py-3 rounded-2xl transition-all font-bold text-sm sm:text-base ${
              activeTab === 'schools' 
                ? 'bg-sky-blue text-white shadow-lg' 
                : 'bg-white text-text-medium hover:bg-gray-50 border-2 border-gray-100'
            }`}
          >
            <School className="w-4 h-4 sm:w-5 sm:h-5" />
            Schools
          </button>
          <button
            onClick={() => setActiveTab('analytics')}
            data-testid="admin-analytics-tab"
            className={`flex items-center gap-1 sm:gap-2 px-4 sm:px-6 py-2.5 sm:py-3 rounded-2xl transition-all font-bold text-sm sm:text-base ${
              activeTab === 'analytics' 
                ? 'bg-mint-green text-white shadow-lg' 
                : 'bg-white text-text-medium hover:bg-gray-50 border-2 border-gray-100'
            }`}
          >
            <BarChart3 className="w-4 h-4 sm:w-5 sm:h-5" />
            Visitors
          </button>
        </div>

        {/* Users Tab */}
        {activeTab === 'users' && (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 sm:gap-6">
            {/* User List */}
            <div className="lg:col-span-2">
              {/* Search */}
              <div className="flex flex-col sm:flex-row gap-2 sm:gap-4 mb-4">
                <div className="flex-1 relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 sm:w-5 sm:h-5 text-text-medium" />
                  <input
                    type="text"
                    placeholder="Search users..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && searchUsers()}
                    className="w-full pl-9 sm:pl-10 pr-3 sm:pr-4 py-2.5 bg-white border-2 border-gray-200 rounded-xl text-text-dark placeholder-text-medium focus:outline-none focus:border-mint-green text-sm sm:text-base"
                  />
                </div>
                <div className="flex gap-2">
                  <select
                    value={userTypeFilter}
                    onChange={(e) => setUserTypeFilter(e.target.value)}
                    className="flex-1 sm:flex-none px-3 sm:px-4 py-2.5 bg-white border-2 border-gray-200 rounded-xl text-text-dark focus:outline-none focus:border-mint-green text-sm sm:text-base"
                  >
                    <option value="">All Types</option>
                    <option value="parent">Parents</option>
                    <option value="pupil">Pupils</option>
                    <option value="teacher">Teachers</option>
                  </select>
                  <button
                    onClick={searchUsers}
                    className="px-4 sm:px-5 py-2.5 bg-mint-green text-white rounded-xl hover:bg-mint-green/90 transition-colors text-sm sm:text-base font-bold shadow-lg"
                  >
                    <Search className="w-4 h-4 sm:hidden" />
                    <span className="hidden sm:inline">Search</span>
                  </button>
                </div>
              </div>

              {/* User Table */}
              <div className="bg-white rounded-2xl sm:rounded-3xl border-2 border-gray-100 shadow-lg overflow-hidden">
                <table className="w-full">
                  <thead className="bg-gray-50 border-b-2 border-gray-100">
                    <tr>
                      <th className="text-left px-4 py-3 text-text-medium font-bold text-sm">User</th>
                      <th className="text-left px-4 py-3 text-text-medium font-bold text-sm">Type</th>
                      <th className="text-left px-4 py-3 text-text-medium font-bold text-sm">Stage</th>
                      <th className="text-left px-4 py-3 text-text-medium font-bold text-sm">Coins</th>
                      <th className="text-right px-4 py-3 text-text-medium font-bold text-sm">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {users.map((user) => (
                      <tr 
                        key={user.id} 
                        className={`border-t border-gray-100 hover:bg-mint-green/5 cursor-pointer transition-colors ${
                          selectedUser?.user?.id === user.id ? 'bg-mint-green/10' : ''
                        }`}
                        onClick={() => viewUserDetails(user.id)}
                      >
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-2">
                            {user.is_admin && <Crown className="w-4 h-4 text-sunny-yellow fill-sunny-yellow" />}
                            <div>
                              <div className="text-text-dark font-bold">{user.child_name || user.username}</div>
                              <div className="text-text-medium text-sm">{user.email || user.username}</div>
                            </div>
                          </div>
                        </td>
                        <td className="px-4 py-3">
                          <span className={`px-2.5 py-1 rounded-full text-xs font-bold ${
                            user.user_type === 'teacher' ? 'bg-sky-blue/20 text-sky-blue' :
                            user.user_type === 'parent' ? 'bg-mint-green/20 text-mint-green' :
                            'bg-sunny-yellow/20 text-sunny-yellow'
                          }`}>
                            {user.user_type}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-text-dark font-bold">{user.stage || 1}</td>
                        <td className="px-4 py-3">
                          <span className="text-sunny-yellow font-bold">{user.credits || 0}</span>
                        </td>
                        <td className="px-4 py-3 text-right">
                          <button
                            onClick={(e) => { e.stopPropagation(); viewUserDetails(user.id); }}
                            className="text-text-medium hover:text-coral-orange p-1 transition-colors"
                          >
                            <ChevronDown className="w-5 h-5" />
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            {/* User Detail Panel */}
            <div className="bg-white rounded-2xl sm:rounded-3xl border-2 border-gray-100 shadow-lg p-5 sm:p-6">
              {selectedUser ? (
                <>
                  <div className="flex items-center justify-between mb-6">
                    <h3 className="text-xl font-display font-bold text-text-dark">User Details</h3>
                    <button onClick={() => setSelectedUser(null)} className="text-text-medium hover:text-coral-orange transition-colors">
                      <ChevronUp className="w-5 h-5" />
                    </button>
                  </div>
                  
                  <div className="space-y-5">
                    <div>
                      <div className="text-text-medium text-sm font-medium mb-1">Username</div>
                      <div className="text-text-dark font-bold">{selectedUser.user.username}</div>
                    </div>
                    <div>
                      <div className="text-text-medium text-sm font-medium mb-1">Email</div>
                      <div className="text-text-dark">{selectedUser.user.email || 'N/A'}</div>
                    </div>
                    <div>
                      <div className="text-text-medium text-sm font-medium mb-1">Child Name</div>
                      <div className="text-text-dark">{selectedUser.user.child_name || 'N/A'}</div>
                    </div>
                    
                    {/* Edit Stage */}
                    <div className="bg-gray-50 rounded-xl p-4">
                      <div className="text-text-medium text-sm font-medium mb-2">Stage (1-6)</div>
                      <div className="flex items-center gap-3">
                        <input
                          type="number"
                          min="1"
                          max="6"
                          value={selectedUser.user.stage || 1}
                          onChange={(e) => {
                            const val = parseInt(e.target.value, 10);
                            if (!isNaN(val) && val >= 1 && val <= 6) {
                              updateUser(selectedUser.user.id, { stage: val });
                            }
                          }}
                          className="w-20 px-3 py-2 bg-white border-2 border-gray-200 rounded-xl text-text-dark text-center font-bold text-xl focus:outline-none focus:border-coral-orange"
                          data-testid="stage-input"
                        />
                        <span className="text-text-medium text-sm">Enter 1-6</span>
                      </div>
                    </div>

                    {/* Edit Coins */}
                    <div className="bg-sunny-yellow/10 rounded-xl p-4">
                      <div className="text-text-medium text-sm font-medium mb-2">Coins</div>
                      <div className="flex items-center gap-2 mb-3">
                        <button
                          onClick={() => updateUser(selectedUser.user.id, { credits: Math.max(0, (selectedUser.user.credits || 0) - 100) })}
                          className="p-2 bg-white border-2 border-gray-200 rounded-xl hover:border-coral-orange transition-colors"
                          data-testid="coins-minus-btn"
                        >
                          <Minus className="w-4 h-4 text-text-dark" />
                        </button>
                        <span className="text-sunny-yellow font-display font-bold text-2xl w-24 text-center" data-testid="user-coins-display">
                          {selectedUser.user.credits || 0}
                        </span>
                        <button
                          onClick={() => updateUser(selectedUser.user.id, { credits: (selectedUser.user.credits || 0) + 100 })}
                          className="p-2 bg-white border-2 border-gray-200 rounded-xl hover:border-mint-green transition-colors"
                          data-testid="coins-plus-btn"
                        >
                          <Plus className="w-4 h-4 text-text-dark" />
                        </button>
                      </div>
                      <div className="text-text-medium text-xs mb-3">Click +/- to add/remove 100 coins</div>
                      
                      {/* Custom Coins Input */}
                      <div className="flex gap-2">
                        <input
                          type="number"
                          placeholder="Enter amount"
                          value={customCoins}
                          onChange={(e) => setCustomCoins(e.target.value)}
                          className="flex-1 px-3 py-2 bg-white border-2 border-gray-200 rounded-xl text-text-dark placeholder-text-medium focus:outline-none focus:border-sunny-yellow text-sm"
                          data-testid="custom-coins-input"
                        />
                        <button
                          onClick={() => {
                            const amount = parseInt(customCoins, 10);
                            if (!isNaN(amount)) {
                              const newCoins = (selectedUser.user.credits || 0) + amount;
                              updateUser(selectedUser.user.id, { credits: Math.max(0, newCoins) });
                              setCustomCoins('');
                            }
                          }}
                          className="px-3 py-2 bg-mint-green text-white rounded-xl hover:bg-mint-green/90 transition-colors text-sm font-bold"
                          data-testid="add-coins-btn"
                        >
                          Add
                        </button>
                        <button
                          onClick={() => {
                            const amount = parseInt(customCoins, 10);
                            if (!isNaN(amount) && amount > 0) {
                              updateUser(selectedUser.user.id, { credits: amount });
                              setCustomCoins('');
                            }
                          }}
                          className="px-3 py-2 bg-sky-blue text-white rounded-xl hover:bg-sky-blue/90 transition-colors text-sm font-bold"
                          data-testid="set-coins-btn"
                        >
                          Set
                        </button>
                      </div>
                      <div className="text-text-medium text-xs mt-2">Add: adds to current • Set: replaces current</div>
                    </div>

                    {/* Admin Toggle */}
                    <div>
                      <div className="text-text-medium text-sm font-medium mb-2">Admin Access</div>
                      <button
                        onClick={() => updateUser(selectedUser.user.id, { is_admin: !selectedUser.user.is_admin })}
                        className={`px-4 py-2.5 rounded-xl font-bold transition-all ${
                          selectedUser.user.is_admin 
                            ? 'bg-coral-orange text-white shadow-lg' 
                            : 'bg-gray-100 text-text-medium hover:bg-gray-200'
                        }`}
                        data-testid="admin-toggle-btn"
                      >
                        {selectedUser.user.is_admin ? '✓ Admin' : 'Make Admin'}
                      </button>
                    </div>

                    {/* Free Forever Toggle */}
                    <div>
                      <div className="text-text-medium text-sm font-medium mb-2">Free Forever Access</div>
                      <button
                        onClick={() => updateUser(selectedUser.user.id, { free_forever: !selectedUser.user.free_forever })}
                        className={`px-4 py-2.5 rounded-xl font-bold transition-all ${
                          selectedUser.user.free_forever 
                            ? 'bg-mint-green text-white shadow-lg' 
                            : 'bg-gray-100 text-text-medium hover:bg-gray-200'
                        }`}
                        data-testid="free-forever-toggle-btn"
                      >
                        {selectedUser.user.free_forever ? '✓ Free Forever' : 'Grant Free Access'}
                      </button>
                      <div className="text-text-medium text-xs mt-2">Grants full free-for-life access</div>
                    </div>

                    {/* Actions */}
                    <div className="pt-4 border-t-2 border-gray-100 space-y-2">
                      <button
                        onClick={() => resetPassword(selectedUser.user.id)}
                        className="w-full flex items-center justify-center gap-2 px-4 py-2.5 bg-gray-100 text-text-dark rounded-xl hover:bg-gray-200 transition-colors font-bold"
                      >
                        <KeyRound className="w-4 h-4" />
                        Reset Password
                      </button>
                      <button
                        onClick={() => deleteUser(selectedUser.user.id, selectedUser.user.username)}
                        className="w-full flex items-center justify-center gap-2 px-4 py-2.5 bg-red-50 text-red-600 rounded-xl hover:bg-red-100 transition-colors font-bold"
                      >
                        <Trash2 className="w-4 h-4" />
                        Delete User
                      </button>
                    </div>
                  </div>
                </>
              ) : (
                <div className="text-center text-text-medium py-12">
                  <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <Users className="w-8 h-8 text-text-medium" />
                  </div>
                  <p className="font-medium">Select a user to view details</p>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Schools Tab */}
        {/* Analytics Tab */}
        {activeTab === 'analytics' && analytics && (
          <div className="space-y-6" data-testid="analytics-panel">
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
              {[['today','Today'],['7d','Last 7 Days'],['30d','Last 30 Days'],['all','All Time']].map(([key,label]) => (
                <div key={key} className="bg-white rounded-2xl p-5 border-2 border-gray-100 shadow-sm" data-testid={`analytics-card-${key}`}>
                  <div className="text-text-medium text-xs font-bold uppercase tracking-wide mb-3">{label}</div>
                  <div className="flex items-center gap-2 mb-1">
                    <Eye className="w-4 h-4 text-sky-blue" />
                    <span className="text-2xl font-display font-bold text-text-dark">{analytics.summary[key].views}</span>
                    <span className="text-xs text-text-medium">views</span>
                  </div>
                  <div className="flex items-center gap-2 mb-1">
                    <Users className="w-4 h-4 text-mint-green" />
                    <span className="text-lg font-bold text-text-dark">{analytics.summary[key].visitors}</span>
                    <span className="text-xs text-text-medium">visitors</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <UserPlus className="w-4 h-4 text-coral-orange" />
                    <span className="text-lg font-bold text-text-dark">{analytics.summary[key].signups}</span>
                    <span className="text-xs text-text-medium">signups</span>
                  </div>
                </div>
              ))}
            </div>

            <div className="bg-white rounded-2xl p-5 border-2 border-gray-100 shadow-sm">
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-display font-bold text-lg text-text-dark">Most Popular Pages</h3>
                <select
                  value={analyticsRange}
                  onChange={(e) => setAnalyticsRange(e.target.value)}
                  data-testid="analytics-range-select"
                  className="px-3 py-2 bg-white border-2 border-gray-200 rounded-xl text-text-dark text-sm focus:outline-none focus:border-mint-green"
                >
                  <option value="today">Today</option>
                  <option value="7d">Last 7 Days</option>
                  <option value="30d">Last 30 Days</option>
                  <option value="all">All Time</option>
                </select>
              </div>
              {(analytics.top_pages[analyticsRange] || []).length === 0 ? (
                <p className="text-text-medium text-sm py-6 text-center">No page views recorded yet for this period.</p>
              ) : (
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-gray-100 text-left text-xs text-text-medium uppercase">
                      <th className="pb-2">Page</th>
                      <th className="pb-2 text-right">Views</th>
                      <th className="pb-2 text-right">Visitors</th>
                    </tr>
                  </thead>
                  <tbody>
                    {analytics.top_pages[analyticsRange].map((p, i) => (
                      <tr key={i} className="border-b border-gray-50" data-testid={`top-page-${i}`}>
                        <td className="py-2 font-mono text-sm text-text-dark">{p.path}</td>
                        <td className="py-2 text-right font-bold text-text-dark">{p.views}</td>
                        <td className="py-2 text-right text-text-medium">{p.visitors}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>

            <div className="bg-white rounded-2xl p-5 border-2 border-gray-100 shadow-sm" data-testid="traffic-sources-panel">
              <h3 className="font-display font-bold text-lg text-text-dark mb-4">Traffic Sources</h3>
              {((analytics.sources && analytics.sources[analyticsRange]) || []).length === 0 ? (
                <p className="text-text-medium text-sm py-6 text-center">No traffic sources recorded yet for this period.</p>
              ) : (
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-gray-100 text-left text-xs text-text-medium uppercase">
                      <th className="pb-2">Source</th>
                      <th className="pb-2 text-right">Visitors</th>
                      <th className="pb-2 text-right">Views</th>
                    </tr>
                  </thead>
                  <tbody>
                    {analytics.sources[analyticsRange].map((s, i) => (
                      <tr key={i} className="border-b border-gray-50" data-testid={`traffic-source-${i}`}>
                        <td className="py-2 text-sm text-text-dark capitalize">{s.source || 'direct'}</td>
                        <td className="py-2 text-right font-bold text-text-dark">{s.visitors}</td>
                        <td className="py-2 text-right text-text-medium">{s.views}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </div>
        )}

        {/* Schools Tab */}
        {activeTab === 'schools' && (
          <div className="bg-white rounded-2xl sm:rounded-3xl border-2 border-gray-100 shadow-lg overflow-hidden">
            <table className="w-full">
              <thead className="bg-gray-50 border-b-2 border-gray-100">
                <tr>
                  <th className="text-left px-4 py-3 text-text-medium font-bold text-sm">School Name</th>
                  <th className="text-left px-4 py-3 text-text-medium font-bold text-sm">Code</th>
                  <th className="text-left px-4 py-3 text-text-medium font-bold text-sm">Teachers</th>
                  <th className="text-left px-4 py-3 text-text-medium font-bold text-sm">Classes</th>
                  <th className="text-left px-4 py-3 text-text-medium font-bold text-sm">Pupils</th>
                  <th className="text-left px-4 py-3 text-text-medium font-bold text-sm">Created</th>
                </tr>
              </thead>
              <tbody>
                {schools.map((school) => (
                  <tr key={school.id} className="border-t border-gray-100 hover:bg-mint-green/5 transition-colors">
                    <td className="px-4 py-3 text-text-dark font-bold">{school.name}</td>
                    <td className="px-4 py-3">
                      <code className="bg-coral-orange/10 text-coral-orange px-2.5 py-1 rounded-lg font-mono font-bold text-sm">{school.code}</code>
                    </td>
                    <td className="px-4 py-3 text-sky-blue font-bold">{school.teacher_count}</td>
                    <td className="px-4 py-3 text-text-dark font-bold">{school.class_count}</td>
                    <td className="px-4 py-3 text-mint-green font-bold">{school.pupil_count}</td>
                    <td className="px-4 py-3 text-text-medium">
                      {new Date(school.created_at).toLocaleDateString()}
                    </td>
                  </tr>
                ))}
                {schools.length === 0 && (
                  <tr>
                    <td colSpan={6} className="px-4 py-12 text-center text-text-medium">
                      <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                        <School className="w-8 h-8 text-text-medium" />
                      </div>
                      <p className="font-medium">No schools registered yet</p>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        )}
      </main>
    </div>
  );
};

export default AdminPage;
